import { ApplicationError } from '@office-start/application-error';
import { ACTION_DETAILS, DataSecurityLevel, DIAGNOSTIC_DETAILS, ERROR_DETAILS, EVENT_CATEGORY, FEATURE_DETAILS, IMPRESSION_DETAILS, PAGE_APP_NAME, PAGE_NAME, PAGE_TYPE, PERFORMANCE_DETAILS, REQUEST_DETAILS, } from '@office-start/telemetry-types';
import { convertRouteToPage, getFlattenedFieldName, getPageAppName, getPageType, } from '@office-start/telemetry-utility';
import { createEventName, getPageName, isPageViewEvent, setPageDetails, } from './wrappers/logPageView';
import { isSessionEvent, logSession, setSessionInfo, } from './wrappers/logSession';
var eventQueue = window.queuedOtelEvents || [];
var routeInitialized = false;
var getCurrentRoute;
window.appSecurityLevel = DataSecurityLevel.Full;
var telemetryServiceInitialized = false;
var flights = '';
/**
 * Function used by other publicly exposed wrapper function to either send
 * the event immediately when all EventSink are registered or buffer it
 * TODO: ADD more unit tests
 *
 * @param eventName Event name
 * @param params Additional data properties for event
 *
 */
export function logEvent(eventName, params, securityThreshold, isSampling) {
    if (params === void 0) { params = {}; }
    if (window.appSecurityLevel < securityThreshold) {
        return;
    }
    if (!window.sessionActive) {
        window.sessionActive = true;
        logSession({ eventName: 'Session_Started' });
    }
    if (window.registeredEventSinks && window.registeredEventSinks.length > 0) {
        window.registeredEventSinks.forEach(function (sink) {
            if (eventName && isEligible(params)) {
                sink.logEvent(eventName, params, isSampling === undefined
                    ? isSamplingEnabled(params)
                    : isSampling, isDiagnostic(params));
            }
        });
    }
    else {
        // PageView event name will be set later in drainQueuedEvents after getCurrentRoute is initialized
        if (eventName || (eventName === undefined && isPageViewEvent(params))) {
            var queuedEvent = {
                eventName: eventName,
                params: params,
                securityThreshold: securityThreshold,
                isSampled: isSampling,
            };
            // push event into eventQueue when isBootstrap is undefined or false
            if (!window.isBootstrap) {
                eventQueue.push(queuedEvent);
            }
            else {
                // queue the Bootstrap events to window.queuedOtelEvents
                if (!window.queuedOtelEvents) {
                    window.queuedOtelEvents = [];
                }
                window.queuedOtelEvents.push(queuedEvent);
            }
        }
    }
}
function drainQueuedEvents() {
    var queuedEvents = eventQueue.splice(0, eventQueue.length);
    var currentPageName = convertRouteToPage(getCurrentRouteItem());
    queuedEvents.forEach(function (event) {
        if (isPageViewEvent(event.params)) {
            // Check and set PageName and EventName for PageView event if not set
            event.params = setPageDetails(event.params);
            event.eventName = createEventName(getPageName(event.params));
        }
        else {
            // Set Flights field
            if (isSessionEvent(event.params)) {
                event.params = setSessionInfo(event.params);
            }
            // Check whether event has pageName or not, if not, set the currentPageName to event PageName
            event.params = checkPageDetails(event.params, currentPageName);
        }
        logEvent(event.eventName, event.params, event.securityThreshold, event.isSampled);
    });
}
export function initializeTelemetryService(sinks, getCurrentRouteFunc, eventTypeConfiguration, eventSamplingConfiguration, initialAppSecurityLevel, initialFlights) {
    if (initialFlights === void 0) { initialFlights = ''; }
    if (window.registeredEventSinks && window.registeredEventSinks.length > 0) {
        throw new ApplicationError('initializeInstrumentationService is not allowed to be called more than once');
    }
    if (!sinks || sinks.length === 0) {
        throw new ApplicationError('Must specify at least one event factory when calling initializeInstrumentation');
    }
    getCurrentRoute = getCurrentRouteFunc;
    routeInitialized = true;
    window.eventCategoryConfig = eventTypeConfiguration;
    window.eventSamplingConfig = eventSamplingConfiguration;
    flights = initialFlights;
    // Make a copy of the passed array
    window.registeredEventSinks = sinks.slice();
    if (typeof initialAppSecurityLevel === 'number') {
        window.appSecurityLevel = initialAppSecurityLevel;
    }
    // Drain queued events for all apps
    drainQueuedEvents();
    telemetryServiceInitialized = true;
}
export function flush() {
    drainQueuedEvents();
    window.registeredEventSinks.forEach(function (sink) {
        sink.flush();
    });
}
// Used for flushing queued events before Telemetry Service is not ready, i.e: Embed Bootstrap error, pwa reloadApp ...etc.
export function flushQueuedEvents(sinks, eventTypeConfiguration, eventSamplingConfiguration, initialAppSecurityLevel) {
    var _a;
    if (!sinks || sinks.length === 0 || telemetryServiceInitialized) {
        return;
    }
    if (typeof initialAppSecurityLevel === 'number') {
        window.appSecurityLevel = initialAppSecurityLevel;
    }
    window.eventCategoryConfig = eventTypeConfiguration;
    window.eventSamplingConfig = eventSamplingConfiguration;
    // If eventQueue has events, then we it should include window.queuedOtelEvents and we don't need to flush
    // window.queuedOtelEvents to prevent duplicate events got flushed
    if (eventQueue === null || eventQueue === void 0 ? void 0 : eventQueue.length) {
        eventQueue
            .splice(0, eventQueue.length)
            .forEach(function (event) {
            if (window.appSecurityLevel >= event.securityThreshold &&
                event.eventName &&
                isEligible(event.params)) {
                sinks.forEach(function (sink) {
                    sink.logEvent(
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    event.eventName, event.params, event.isSampled === undefined
                        ? isSamplingEnabled(event.params)
                        : event.isSampled, isDiagnostic(event.params));
                });
            }
        });
    }
    else {
        // if eventQueue is empty, we check window.queuedOtelEvents and see if there are events queued in window.queuedOtelEvents
        if ((_a = window.queuedOtelEvents) === null || _a === void 0 ? void 0 : _a.length) {
            window.queuedOtelEvents
                .splice(0, window.queuedOtelEvents.length)
                .forEach(function (event) {
                if (window.appSecurityLevel >= event.securityThreshold &&
                    event.eventName &&
                    isEligible(event.params)) {
                    sinks.forEach(function (sink) {
                        sink.logEvent(
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        event.eventName, event.params, event.isSampled === undefined
                            ? isSamplingEnabled(event.params)
                            : event.isSampled, isDiagnostic(event.params));
                    });
                }
            });
        }
    }
}
// This function is used by unit test to unregister event sinks
export function shutdown() {
    var _a;
    (_a = window.registeredEventSinks) === null || _a === void 0 ? void 0 : _a.slice(0, window.registeredEventSinks.length);
}
export function isRouteInitialized() {
    return routeInitialized;
}
export function getCurrentRouteItem() {
    return getCurrentRoute();
}
export function isEligible(params) {
    switch (params[EVENT_CATEGORY]) {
        case "Action" /* EventCategory.Action */:
            return window.eventCategoryConfig.actionEnabled;
        case "Diagnostic" /* EventCategory.Diagnostic */:
            return window.eventCategoryConfig.diagnosticEnabled;
        case "Error" /* EventCategory.Error */:
            return window.eventCategoryConfig.errorEnabled;
        case "Feature" /* EventCategory.Feature */:
            return window.eventCategoryConfig.featureEnabled;
        case "Impression" /* EventCategory.Impression */:
            return window.eventCategoryConfig.impressionEnabled;
        case "PageView" /* EventCategory.PageView */:
            return window.eventCategoryConfig.pageViewEnabled;
        case "Performance" /* EventCategory.Performance */:
            return window.eventCategoryConfig.perfEnabled;
        case "Session" /* EventCategory.Session */:
            return true;
        case "RequestSampled" /* EventCategory.RequestSampled */:
        case "Request" /* EventCategory.Request */:
            return window.eventCategoryConfig.requestEnabled;
        default:
            return false;
    }
}
export function isSamplingEnabled(params) {
    switch (params[EVENT_CATEGORY]) {
        case "Performance" /* EventCategory.Performance */:
            return window.eventSamplingConfig.perfSamplingEnabled;
        case "Request" /* EventCategory.Request */:
            return window.eventSamplingConfig.requestSamplingEnabled;
        case "RequestSampled" /* EventCategory.RequestSampled */:
            return true;
        default:
            return false;
    }
}
export function isDiagnostic(params) {
    return params[EVENT_CATEGORY] === "Diagnostic" /* EventCategory.Diagnostic */;
}
export function updateAppSecurityLevel(level) {
    window.appSecurityLevel = level;
}
export function isTelemetryServiceInitialized() {
    return telemetryServiceInitialized;
}
export function getFlights() {
    return flights;
}
export function checkPageDetails(params, currentPageName) {
    switch (params[EVENT_CATEGORY]) {
        case "Action" /* EventCategory.Action */:
            return setEventPageDetail(params, ACTION_DETAILS, currentPageName);
        case "Diagnostic" /* EventCategory.Diagnostic */:
            return setEventPageDetail(params, DIAGNOSTIC_DETAILS, currentPageName);
        case "Error" /* EventCategory.Error */:
            return setEventPageDetail(params, ERROR_DETAILS, currentPageName);
        case "Feature" /* EventCategory.Feature */:
            return setEventPageDetail(params, FEATURE_DETAILS, currentPageName);
        case "Impression" /* EventCategory.Impression */:
            return setEventPageDetail(params, IMPRESSION_DETAILS, currentPageName);
        case "Performance" /* EventCategory.Performance */:
            return setEventPageDetail(params, PERFORMANCE_DETAILS, currentPageName);
        case "Request" /* EventCategory.Request */:
            return setEventPageDetail(params, REQUEST_DETAILS, currentPageName);
        default:
            return params;
    }
}
export function setEventPageDetail(params, detailEventName, currentPageName) {
    if (params[getFlattenedFieldName([detailEventName, PAGE_NAME])]) {
        return params;
    }
    params[getFlattenedFieldName([detailEventName, PAGE_NAME])] =
        currentPageName;
    params[getFlattenedFieldName([detailEventName, PAGE_TYPE])] =
        getPageType(currentPageName);
    params[getFlattenedFieldName([detailEventName, PAGE_APP_NAME])] =
        getPageAppName(currentPageName);
    return params;
}
//# sourceMappingURL=TelemetryService.js.map